import React from 'react';
import analyticsService from '../lib/AnalyticsService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to analytics
    this.setState({
      error,
      errorInfo
    });

    // Track the error with our analytics service
    analyticsService.trackError('react_error_boundary', error, {
      error_boundary: true,
      component_stack: errorInfo.componentStack,
      fatal: true,
      props: this.props.trackingProps || {}
    });

    // Also log to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    // Track the retry attempt
    analyticsService.trackEvent('Error Boundary Retry', {
      previous_error: this.state.error?.message,
      retry_timestamp: new Date().toISOString()
    });

    // Reset the error state
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReportError = () => {
    // Track error reporting
    analyticsService.trackEvent('Error Reported', {
      error_message: this.state.error?.message,
      error_stack: this.state.error?.stack,
      component_stack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString()
    });

    // You could integrate with a bug reporting service here
    alert('Error reported. Thank you for helping us improve!');
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Oops! Something went wrong
              </h2>
              <p className="text-gray-600 mb-6">
                We encountered an unexpected error. Our team has been notified and is working on a fix.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh Page
              </button>

              {process.env.NODE_ENV === 'development' && (
                <div className="mt-6 text-left">
                  <details className="text-sm">
                    <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                      Error Details (Development)
                    </summary>
                    <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono overflow-auto max-h-40">
                      <div className="font-bold text-red-600 mb-2">Error:</div>
                      <div className="mb-3">{this.state.error?.toString()}</div>
                      
                      <div className="font-bold text-red-600 mb-2">Stack Trace:</div>
                      <div className="whitespace-pre-wrap">{this.state.error?.stack}</div>
                      
                      <div className="font-bold text-red-600 mb-2 mt-3">Component Stack:</div>
                      <div className="whitespace-pre-wrap">{this.state.errorInfo?.componentStack}</div>
                    </div>
                  </details>
                </div>
              )}
            </div>

            <div className="mt-6 text-xs text-gray-500">
              Error ID: {Date.now().toString(36)}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
